import type { WebTheme } from '@/types';
import { handleOpacityApplication } from '@/utils/handleOpacityApplication';
import { transformTokenOverrides } from '@/utils/transformThemeOverrides';

import type { NavbarSerializableNode } from '../Serializer.types';

export type NodeAttrsToThemePropertiesMap = Record<string, Partial<Record<keyof WebTheme, string[]>>>;

export const applyThemeToNavbarNode = (
  node: Partial<NavbarSerializableNode>,
  theme: WebTheme,
  mapping: NodeAttrsToThemePropertiesMap,
  applyToOldNode?: boolean,
): NavbarSerializableNode => {
  if (!mapping) {
    return node as NavbarSerializableNode;
  }
  const newNode = { ...node } as NavbarSerializableNode;

  let nodeTypeKey = node.type as keyof typeof mapping;
  if (node.attrs && 'type' in node.attrs && node.attrs?.type) {
    nodeTypeKey = `${node.type}.${node.attrs.type}`;
  }

  if (nodeTypeKey in mapping) {
    const nodeThemeMapping = transformTokenOverrides(node.attrs?.tokens || {}, mapping[nodeTypeKey]);
    if (!nodeThemeMapping) {
      return newNode;
    }
    const themeAttrKeys = Object.keys(nodeThemeMapping);
    const updatedNodeAttrs: Record<string, string> = {};

    for (const attrKey of themeAttrKeys) {
      if (!Object?.hasOwn(theme, attrKey)) {
        continue;
      }

      // We determined that updating border radius was too intrusive to be applied here
      // Too many edge cases to account for for this to be smooth without introducing more theme properties
      // So it makes more sense to skip this in the theme applier.
      if (attrKey === 'border_radius') continue;

      const themeValue = theme[attrKey as keyof WebTheme];
      const attrsToUpdate = nodeThemeMapping[attrKey as keyof WebTheme];

      for (const attr of attrsToUpdate) {
        const adjustedValue = handleOpacityApplication({
          incomingKey: attrKey,
          previousValue: node.attrs?.[attr as keyof typeof node.attrs] as string,
          newValue: themeValue as string,
        });

        updatedNodeAttrs[attr] = adjustedValue;
      }
    }

    if (Object.keys(updatedNodeAttrs).length > 0) {
      newNode.attrs = {
        ...(newNode.attrs || {}),
        ...updatedNodeAttrs,
      } as NavbarSerializableNode['attrs'];
      if (applyToOldNode) {
        node.attrs = {
          ...(node.attrs || {}),
          ...updatedNodeAttrs,
        } as NavbarSerializableNode['attrs'];
      }
    }
  }

  if ('content' in newNode && Array.isArray(newNode.content)) {
    const newContent = newNode.content.map((child) =>
      applyThemeToNavbarNode(child, theme, mapping, applyToOldNode),
    ) as typeof newNode.content;
    newNode.content = newContent;
  }

  return newNode as NavbarSerializableNode;
};
